/**
 * RoleIndicator - Displays a badge showing the user's account type with tooltip.
 * 
 * Renders a colored badge based on accountType from secure calie_account_types table:
 * - platform_admin: Purple badge with "Admin" label
 * - agency: Indigo badge with "Agency" label
 * - business_owner: Blue badge with "Business" label
 * 
 * Hidden on mobile screens as the AccountTypeCard provides this info on the Profile page.
 */
import React from "react";
import { useAuth } from "@/hooks/useAuth";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const ROLE_STYLES = {
  admin: {
    color: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300",
    label: "Admin",
    tooltip: "Platform administrator with full access"
  },
  agency: {
    color: "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300",
    label: "Agency",
    tooltip: "Using your own Twilio account"
  },
  business_owner: {
    color: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
    label: "Business",
    tooltip: "Using Calie.ai-provisioned Twilio account"
  }
} as const;

export const RoleIndicator = () => {
  const { accountType, loading } = useAuth();
  
  if (loading || !accountType) return null;
  
  // Map accountType to display style key
  const styleKey = accountType === 'platform_admin' ? 'admin' : accountType;
  const config = ROLE_STYLES[styleKey as keyof typeof ROLE_STYLES] || ROLE_STYLES.business_owner;
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge variant="outline" className={`ml-2 ${config.color}`}>
            {config.label}
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <p>{config.tooltip}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default RoleIndicator;
